import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IManagerLink,AuditEntityType, ICreateManagerLink, IDeleteManagerLink, IDeleteManagerLinkResponse } from '@sr/dto';
import { fetchLinkedManagers, postLinkManager, deleteLinkManager } from 'entities/company/linked-managers/api';
import { LoadingStatus } from 'shared/api/async-meta';
import { invalidateEntityAudit } from 'entities/audit/utils';
import { RootState } from 'shared/store/types';


export type State = {
  status: LoadingStatus;
  error: string;
  postNewLinkStatus: LoadingStatus;
  links: IManagerLink[];
};

const initialState: State = {
  status: null,
  error: '',
  postNewLinkStatus: null,
  links: [],
};

export const loadThunk = createAsyncThunk<IManagerLink[], number, { rejectValue: string; }>(
  'loadLinks',
  async (companyId: number, thunkAPI) => fetchLinkedManagers(companyId)
);

export const postNewLinkThunk = createAsyncThunk<IManagerLink, ICreateManagerLink>(
  'addLink',
  async (data: ICreateManagerLink, thunkAPI) => {
    const result = await postLinkManager(data);
    invalidateEntityAudit(thunkAPI.dispatch, AuditEntityType.COMPANY, data.companyId);
    return result;
  }
);

export const deleteLinkThunk = createAsyncThunk<IDeleteManagerLinkResponse, IDeleteManagerLink>(
  'deleteLink',
  async (data: IDeleteManagerLink, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const linkToDelete = state.companyLinkedManagers.links.find((link) => link.id === data.linkId);
      const companyId = linkToDelete?.companyId;
      const response = await deleteLinkManager(data);
      if (companyId) {
        invalidateEntityAudit(thunkAPI.dispatch, AuditEntityType.COMPANY, companyId);
      }
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const slice = createSlice({
  name: 'linkedManagers',
  initialState: initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder.addCase(loadThunk.pending, (state, action) => {
      state.status = 'loading';
      state.links = [];
    });
    builder.addCase(loadThunk.fulfilled, (state, action) => {
      state.status = 'done';
      state.links = action.payload;
    });
    builder.addCase(loadThunk.rejected, (state, action) => {
      state.status = 'error';
      state.error = action.error.message || '';
      state.links = [];
    });


    builder.addCase(postNewLinkThunk.pending, (state) => {
      state.postNewLinkStatus = 'loading';
    });
    builder.addCase(postNewLinkThunk.fulfilled, (state, action) => {
      state.postNewLinkStatus = 'done';
      state.links.push(action.payload);
    });
    builder.addCase(postNewLinkThunk.rejected, (state, action) => {
      state.postNewLinkStatus = 'error';
      state.error = action.error.message || '';
    });

    builder.addCase(deleteLinkThunk.pending, (state) => {
      state.postNewLinkStatus = 'loading';
    });
    builder.addCase(deleteLinkThunk.fulfilled, (state, action) => {
      state.postNewLinkStatus = 'done';
      state.links = state.links.filter(x => x.id !== action.payload.linkId);
    });
    builder.addCase(deleteLinkThunk.rejected, (state, action) => {
      state.postNewLinkStatus = 'error';
      state.error = action.error.message || '';
    });

  }

});

export const { reducer: linkedManagersReducer, actions: linkedManagersActions } = slice;
export const linkedManagersThunks = {
  loadThunk, postNewLinkThunk, deleteLinkThunk
};
